import { useTranslation } from 'react-i18next'
import { ProjectProvider } from '../context/ProjectContext'
import { ProjectViewHeader } from './ProjectViewHeader'
import { ProjectViewRenderer } from './ProjectViewRenderer'
import type { SavedFilterDocument } from '@/lib/db/rxdb/schemas/savedFilter'
import type { HydrationStatus } from '@/routes/features/views/useViewHydrator'
import type { ProjectTabValue } from '../types'
import { Button } from '@/components/ui/button'
import { FilterBar } from '@/components/features/filters'

interface ProjectSavedViewShellProps {
  projectId: string
  status: Exclude<HydrationStatus, 'pending'>
  view: SavedFilterDocument | null
  onTabChange: (tab: ProjectTabValue) => void
  onBackToViews: () => void
}

export function ProjectSavedViewShell({
  projectId,
  status,
  view,
  onTabChange,
  onBackToViews,
}: ProjectSavedViewShellProps) {
  const { t } = useTranslation('common')

  if (status === 'missing' || status === 'forbidden') {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="flex-shrink-0">
          <ProjectViewHeader
            projectId={projectId}
            activeTab="views"
            onTabChange={onTabChange}
          />
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {status === 'forbidden'
              ? t('views.errors.forbidden', {
                  defaultValue: 'You do not have access to this view.',
                })
              : t('views.errors.missing', {
                  defaultValue: 'This view no longer exists.',
                })}
          </p>
          <Button size="sm" variant="outline" onClick={onBackToViews}>
            {t('views.backToViews', { defaultValue: 'Back to views' })}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <ProjectProvider projectId={projectId}>
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="flex-shrink-0">
          <ProjectViewHeader
            projectId={projectId}
            activeTab="views"
            onTabChange={onTabChange}
          />
        </div>
        <div className="flex-shrink-0">
          <FilterBar
            projectId={projectId}
            hiddenFields={['project']}
            currentView={view}
          />
        </div>
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
          <ProjectViewRenderer projectId={projectId} hideProjectFilter />
        </div>
      </div>
    </ProjectProvider>
  )
}
